import { createContext, useContext, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn'

type TabsContextValue = {
  active: string
  setActive: (id: string) => void
}

const TabsContext = createContext<TabsContextValue | null>(null)

export type TabsProps = {
  defaultValue: string
  value?: string
  onValueChange?: (value: string) => void
  children: ReactNode
  className?: string
}

export function Tabs({
  defaultValue,
  value,
  onValueChange,
  children,
  className,
}: TabsProps) {
  const [internal, setInternal] = useState(defaultValue)
  const active = value ?? internal

  function setActive(next: string) {
    if (value === undefined) setInternal(next)
    onValueChange?.(next)
  }

  return (
    <TabsContext.Provider value={{ active, setActive }}>
      <div className={cn('space-y-4', className)}>{children}</div>
    </TabsContext.Provider>
  )
}

export function TabsList({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      role="tablist"
      className={cn(
        'inline-flex flex-wrap gap-1 rounded-lg border border-border-default bg-surface-workspace p-1',
        className,
      )}
    >
      {children}
    </div>
  )
}

export type TabsTriggerProps = {
  value: string
  children: ReactNode
  className?: string
}

export function TabsTrigger({ value, children, className }: TabsTriggerProps) {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('TabsTrigger must be used within Tabs')

  const selected = ctx.active === value
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      className={cn(
        'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
        selected
          ? 'bg-surface-card text-text-primary shadow-sm'
          : 'text-text-muted hover:text-text-primary',
        className,
      )}
      onClick={() => ctx.setActive(value)}
    >
      {children}
    </button>
  )
}

export type TabsContentProps = {
  value: string
  children: ReactNode
  className?: string
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('TabsContent must be used within Tabs')
  if (ctx.active !== value) return null

  return (
    <div role="tabpanel" className={className}>
      {children}
    </div>
  )
}
